import { type ReactNode, useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Building2, Users, LayoutDashboard, FolderOpen, ClipboardList,
  KanbanSquare, Settings2, Pin, BarChart2, Menu, X,
} from 'lucide-react';
import { UserButton } from '@clerk/clerk-react';
import { useTranslation } from 'react-i18next';
import { useWorkspaceStore } from '../../stores/workspace.js';
import { useViews } from '../../hooks/useViews.js';
import SearchBar from '../search/SearchBar.js';
import CommandPalette from '../search/CommandPalette.js';
import NotificationBell from '../notifications/NotificationBell.js';

interface ShellProps {
  children: ReactNode;
}

const ENTITY_ROUTES: Record<string, string> = {
  order: '/orders',
  company: '/companies',
  contact: '/contacts',
  bucket: '/projects',
};

function useBreakpoint() {
  const [width, setWidth] = useState(window.innerWidth);
  useEffect(() => {
    const handle = () => setWidth(window.innerWidth);
    window.addEventListener('resize', handle);
    return () => window.removeEventListener('resize', handle);
  }, []);
  return {
    isMobile: width < 768,
    isTablet: width >= 768 && width < 1024,
    isDesktop: width >= 1024,
  };
}

export default function Shell({ children }: ShellProps) {
  const { t } = useTranslation('common');
  const workspace = useWorkspaceStore((s) => s.workspace);
  const navigate = useNavigate();
  const location = useLocation();
  const { data: pinnedViews = [] } = useViews();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null) as React.RefObject<HTMLInputElement>;
  const { isMobile, isTablet } = useBreakpoint();

  // Close overlay sidebar on route change
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t('nav.dashboard') },
    { to: '/companies', icon: Building2, label: t('nav.companies') },
    { to: '/contacts', icon: Users, label: t('nav.contacts', 'Contacts') },
    { to: '/projects', icon: FolderOpen, label: workspace?.bucketLabelPlural ?? t('nav.projects', 'Projects') },
    { to: '/orders', icon: ClipboardList, label: workspace?.orderLabelPlural ?? t('nav.orders', 'Orders') },
    { to: '/board', icon: KanbanSquare, label: t('nav.board', 'Board') },
    { to: '/reports', icon: BarChart2, label: t('nav.reports') },
    { to: '/settings', icon: Settings2, label: t('nav.settings') },
  ];

  // Keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((open) => !open);
        return;
      }
      const tag = (e.target as HTMLElement)?.tagName;
      const isInput = tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable;
      if (e.key === '/' && !isInput) {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  const sidebarContent = (
    <>
      {/* Logo */}
      <div style={{
        height: 'var(--header-height)',
        display: 'flex', alignItems: 'center',
        padding: '0 var(--space-4)',
        borderBottom: '1px solid var(--color-sidebar-border)',
        gap: 'var(--space-2)',
        justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <svg viewBox="0 0 36 36" width="28" height="28" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
            <rect width="36" height="36" rx="9" fill="#7C3AED"/>
            <circle cx="8" cy="18" r="5" fill="#5B21B6"/>
            <circle cx="18" cy="18" r="5" fill="#FFFFFF"/>
            <circle cx="28" cy="18" r="5" fill="#C4B5FD"/>
          </svg>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: '20px', fontWeight: 300, letterSpacing: '-0.03em', color: 'var(--color-text)' }}>
            mayksel
          </span>
        </div>
        {/* Close button on tablet/mobile overlay */}
        {(isTablet || isMobile) && (
          <button
            type="button"
            onClick={() => setSidebarOpen(false)}
            style={{ padding: 'var(--space-1)', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center' }}
          >
            <X size={18} />
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav style={{ flex: 1, padding: 'var(--space-3) var(--space-2)', overflowY: 'auto' }}>
        {navItems.map(({ to, icon: Icon, label }) => (
          <NavLink
            key={to}
            to={to}
            style={({ isActive }) => ({
              display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
              padding: 'var(--space-2) var(--space-3)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              fontWeight: isActive ? 'var(--weight-medium)' : 'var(--weight-regular)',
              color: isActive ? 'var(--color-brand)' : 'var(--color-text-secondary)',
              background: isActive ? 'var(--color-sidebar-active)' : 'transparent',
              marginBottom: 'var(--space-1)',
              transition: `background var(--duration-snap) var(--ease-out), color var(--duration-snap) var(--ease-out)`,
              textDecoration: 'none',
            })}
          >
            <Icon size={18} />
            {label}
          </NavLink>
        ))}

        {pinnedViews.filter((v) => v.isPinned).length > 0 && (
          <div style={{ marginTop: 'var(--space-4)', paddingTop: 'var(--space-3)', borderTop: '1px solid var(--color-sidebar-border)' }}>
            <p style={{ fontSize: '11px', fontWeight: 'var(--weight-medium)', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '0 var(--space-3)', marginBottom: 'var(--space-2)' }}>
              {t('nav.pinned_views', 'Pinned')}
            </p>
            {pinnedViews.filter((v) => v.isPinned).map((view) => (
              <button
                key={view.id}
                onClick={() => navigate(ENTITY_ROUTES[view.entityType] ?? '/')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 'var(--space-2)',
                  width: '100%', padding: 'var(--space-2) var(--space-3)',
                  background: 'none', border: 'none', cursor: 'pointer',
                  borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-1)',
                  fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', textAlign: 'left',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--color-sidebar-active)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
              >
                <Pin size={14} style={{ flexShrink: 0 }} />
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{view.name}</span>
              </button>
            ))}
          </div>
        )}
      </nav>

      <div style={{ padding: 'var(--space-4)', borderTop: '1px solid var(--color-sidebar-border)' }}>
        <UserButton />
      </div>
    </>
  );

  // ── Mobile bottom tab bar ───────────────────────────────────────────────────
  const mobileTabItems = navItems.slice(0, 5); // Dashboard, Companies, Contacts, Projects, Orders

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: 'var(--color-bg)' }}>
        {/* Overlay sidebar when open */}
        {sidebarOpen && (
          <>
            <div
              onClick={() => setSidebarOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100 }}
            />
            <aside style={{
              position: 'fixed', top: 0, left: 0, bottom: 0, width: 240,
              background: 'var(--color-sidebar-bg)', borderRight: '1px solid var(--color-sidebar-border)',
              display: 'flex', flexDirection: 'column', zIndex: 101,
            }}>
              {sidebarContent}
            </aside>
          </>
        )}

        {/* Main content area */}
        <header style={{
          height: 'var(--header-height)', display: 'flex', alignItems: 'center',
          padding: '0 var(--space-4)', borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg)', gap: 'var(--space-3)', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            style={{ padding: 'var(--space-1)', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center' }}
          >
            <Menu size={20} />
          </button>
          <svg viewBox="0 0 36 36" width="28" height="28" xmlns="http://www.w3.org/2000/svg">
            <rect width="36" height="36" rx="9" fill="#7C3AED"/>
            <circle cx="8" cy="18" r="5" fill="#5B21B6"/>
            <circle cx="18" cy="18" r="5" fill="#FFFFFF"/>
            <circle cx="28" cy="18" r="5" fill="#C4B5FD"/>
          </svg>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <NotificationBell />
            <UserButton />
          </div>
        </header>

        <main style={{ flex: 1, overflow: 'auto' }}>
          <div className="page-content page-enter">{children}</div>
        </main>

        {/* Bottom tab bar */}
        <nav style={{
          display: 'flex', borderTop: '1px solid var(--color-border)',
          background: 'var(--color-bg)', flexShrink: 0,
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}>
          {mobileTabItems.map(({ to, icon: Icon, label }) => (
            <NavLink
              key={to}
              to={to}
              style={({ isActive }) => ({
                flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
                padding: 'var(--space-2) 0 var(--space-1)',
                color: isActive ? 'var(--color-brand)' : 'var(--color-text-muted)',
                textDecoration: 'none', fontSize: 9, gap: 2,
              })}
            >
              <Icon size={20} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />
      </div>
    );
  }

  // ── Tablet: overlay sidebar ─────────────────────────────────────────────────
  if (isTablet) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', background: 'var(--color-bg)' }}>
        {sidebarOpen && (
          <>
            <div
              onClick={() => setSidebarOpen(false)}
              style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 100 }}
            />
            <aside style={{
              position: 'fixed', top: 0, left: 0, bottom: 0, width: 240,
              background: 'var(--color-sidebar-bg)', borderRight: '1px solid var(--color-sidebar-border)',
              display: 'flex', flexDirection: 'column', zIndex: 101,
            }}>
              {sidebarContent}
            </aside>
          </>
        )}

        <header style={{
          height: 'var(--header-height)', display: 'flex', alignItems: 'center',
          padding: '0 var(--space-5)', borderBottom: '1px solid var(--color-border)',
          background: 'var(--color-bg)', gap: 'var(--space-3)', flexShrink: 0,
        }}>
          <button
            type="button"
            onClick={() => setSidebarOpen(true)}
            style={{ padding: 'var(--space-1)', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-text-secondary)', display: 'flex', alignItems: 'center' }}
          >
            <Menu size={20} />
          </button>
          <div style={{ flex: 1 }}>
            <SearchBar inputRef={searchInputRef} />
          </div>
          <NotificationBell />
        </header>

        <main style={{ flex: 1, overflow: 'auto' }}>
          <div className="page-content page-enter">{children}</div>
        </main>

        <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />
      </div>
    );
  }

  // ── Desktop: persistent sidebar ─────────────────────────────────────────────
  return (
    <div className="app-shell">
      <aside className="app-sidebar" style={{ display: 'flex', flexDirection: 'column' }}>
        {sidebarContent}
      </aside>

      <main className="app-main">
        <header
          className="app-header"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', padding: '0 var(--space-5)', gap: 'var(--space-3)' }}
        >
          <SearchBar inputRef={searchInputRef} />
          <NotificationBell />
        </header>
        <div className="page-content page-enter">{children}</div>
      </main>

      <CommandPalette isOpen={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </div>
  );
}
